import moment, { type Moment } from 'moment/moment'
import {
  type PartnerPickRecurringShiftCreateMutationVariables,
  type PartnerPickRecurringShiftUpdateMutationVariables,
  ShiftTypeEnum,
} from 'domains/partner-pick-devices/api'
import { AllocationType, ShiftType } from 'domains/shifts/components/types'
import {
  type DayOfWeekOption,
  type PartnerPickRecurringShiftsFormData,
  type TimeDropdownOptionType,
} from './recurringShift.types'

const getShiftTypeEnumFromLegacyShiftType = (shiftTypeObj: ShiftType): ShiftTypeEnum => {
  if (shiftTypeObj === ShiftType.Picking) {
    return ShiftTypeEnum.PickingOnly
  }
  if (shiftTypeObj === ShiftType.Running) {
    return ShiftTypeEnum.Runner
  }
  if (shiftTypeObj === ShiftType.FullService) {
    return ShiftTypeEnum.FullService
  }
  console.warn('Invalid shift type', shiftTypeObj)
}

export const mapPartnerPickRecurringShiftFormDataToCreateMutationPayload: (
  formData: PartnerPickRecurringShiftsFormData,
  warehouseLocationId: string,
  shiftType: ShiftType,
  shopperId: number,
  recurStartDate: Moment
) => PartnerPickRecurringShiftCreateMutationVariables = (
  formData,
  warehouseLocationId,
  shiftType,
  shopperId,
  recurStartDate
) => {
  let shiftLengthSeconds = formData.to.value - formData.from.value
  if (shiftLengthSeconds < 0) {
    // When the end time is at midnight, the to - from value will be negative
    // We will add a day to the to value, hence the negative number  + 24 hours here
    shiftLengthSeconds = shiftLengthSeconds + 60 * 60 * 24
  }

  const data = {
    warehouseLocationId,
    shiftType: getShiftTypeEnumFromLegacyShiftType(shiftType),
    driverId: shopperId.toString(),
    shiftStartSecondsSinceMidnight: formData.from.value,
    shiftLengthSeconds,
    daysOfWeek: formData.daysOfWeek.map(obj => obj.value) || [],
    recurStartDate: moment(recurStartDate).toISOString(),
  }
  if (formData.recurEndDate) {
    data['recurEndDate'] = moment(formData.recurEndDate).toISOString()
  }
  if (formData.shiftAllocationType) {
    data['onCall'] = formData.shiftAllocationType === AllocationType.OnCall
  }

  return data
}

export const mapPartnerPickRecurringShiftFormDataToUpdateMutationPayload: (
  formData: PartnerPickRecurringShiftsFormData,
  warehouseLocationId: string,
  shiftId: number,
  shiftType: ShiftType,
  shopperId: number,
  recurStartDate: Moment
) => PartnerPickRecurringShiftUpdateMutationVariables = (
  formData,
  warehouseLocationId,
  recurringShiftId,
  shiftType,
  shopperId,
  recurStartDate
) => {
  const data = {
    // only include updated fields here, map form data to recurring shift payload
    driverId: shopperId,
    shiftType: getShiftTypeEnumFromLegacyShiftType(shiftType),
    warehouseLocationId,
    id: recurringShiftId?.toString(),
    shiftStartSecondsSinceMidnight: formData.from.value,
    shiftLengthSeconds: formData.to.value - formData.from.value,
    daysOfWeek: formData.daysOfWeek.map(obj => obj.value) || [],
    recurStartDate: moment(recurStartDate).toISOString(),
  }
  if (formData.recurEndDate) {
    data['recurEndDate'] = moment(formData.recurEndDate).toISOString()
  }
  if (formData.shiftAllocationType) {
    data['onCall'] = formData.shiftAllocationType === AllocationType.OnCall
  }
  return data
}

export const TIME_DISPLAY_FORMAT = 'hh:mm a'

export const getSecondsSinceMidnight = (fromMoment: Moment): number => {
  const momentAtMidnight = fromMoment.clone().startOf('day')
  return fromMoment.diff(momentAtMidnight, 'seconds')
}

export const getTimeOption = (time: Date | Moment, zoneOffset: number): TimeDropdownOptionType => {
  const timeMoment = moment(time)
  const adjustedTime = moment.utc(timeMoment).add(zoneOffset, 'h')
  return {
    label: adjustedTime.format(TIME_DISPLAY_FORMAT),
    value: getSecondsSinceMidnight(adjustedTime),
  }
}

// this method is called from a class component (ShiftCell.tsx hence we can't use the useDashMessage
// hook here to get the labels for daysOfWeek values.
export const getPartnerPickRecurringShiftsFormData = (
  zoneOffset: number,
  from: Date,
  to: Date,
  daysOfWeek: number[], // this comes from legacy GET /shopper_shifts API
  onCall: boolean,
  recurStartDate?: string,
  recurEndDate?: string
): PartnerPickRecurringShiftsFormData => {
  const formData = {
    from: getTimeOption(from, zoneOffset),
    to: getTimeOption(to, zoneOffset),
    // repeatShift: is derived in the form
    daysOfWeek: getDaysOfWeekFromNumbersArr(daysOfWeek),
    recurStartDate: recurStartDate ? moment(recurStartDate) : undefined,
    recurEndDate: recurEndDate ? moment(recurEndDate) : undefined,
    shiftAllocationType: onCall ? AllocationType.OnCall : AllocationType.Dedicated,
  }
  return formData
}

const DAYS_OF_WEEK_CHOICES = [
  { label: 'Sunday', value: 0 },
  { label: 'Monday', value: 1 },
  { label: 'Tuesday', value: 2 },
  { label: 'Wednesday', value: 3 },
  { label: 'Thursday', value: 4 },
  { label: 'Friday', value: 5 },
  { label: 'Saturday', value: 6 },
]

/// remove this once ShiftCell.tsx is converted to a functional compnent and we use use the same method from usePartnerPickRecurringShiftFormOptions
const getDaysOfWeekFromNumbersArr = (daysOfWeekNumbers: number[]): DayOfWeekOption[] =>
  <DayOfWeekOption[]>DAYS_OF_WEEK_CHOICES.filter(day => daysOfWeekNumbers?.includes(day.value))

export const getPartnerPickRecurringShiftsFormDataForNewShiftCreate = (
  zoneOffset: number,
  from,
  to
): PartnerPickRecurringShiftsFormData => ({
  from: getTimeOption(from, zoneOffset),
  to: getTimeOption(to, zoneOffset),
  // repeatShift: is derived in the form
  daysOfWeek: [],
  recurEndDate: undefined,
  shiftAllocationType: AllocationType.Dedicated,
})

export const getDatesFromTimeDropdownOptionType = (
  from: TimeDropdownOptionType,
  to: TimeDropdownOptionType,
  date: string
): { start: Date; end: Date } => ({
  start: moment(date).add(from.value, 'seconds').toDate(),
  end: moment(date)
    .add(from.value + to.value, 'seconds')
    .toDate(),
})

const _getRecursOnDaysDescription = (daysOfWeek: number[]) => {
  if (!daysOfWeek || daysOfWeek.length === 0 || daysOfWeek.length > 7) {
    return daysOfWeek?.join(',')
  }
  if (daysOfWeek.length === 7) {
    return 'Daily'
  }

  if (daysOfWeek.length === 5) {
    for (let i = 0; i < daysOfWeek.length; i++) {
      const A = DAYS_OF_WEEK_CHOICES.map(obj => obj.value).sort()
      const B = daysOfWeek.sort()
      if (A[i] !== B[i]) {
        break
      }
    }
    return 'Every Weekday'
  }

  const recursOnDays = []
  DAYS_OF_WEEK_CHOICES.forEach(obj => {
    if (daysOfWeek.includes(obj.value)) {
      recursOnDays.push(obj.label.substr(0, 3))
    }
  })
  return 'Every ' + recursOnDays.join(', ')
}

export const getRecurringShiftDescription = ({
  shiftStartSecondsSinceMidnight,
  shiftLengthSeconds,
  daysOfWeek,
  date,
  format = 'h:mm a',
}: {
  shiftStartSecondsSinceMidnight: number
  shiftLengthSeconds: number
  daysOfWeek: number[]
  date: string
  format?: string
}) => {
  const start = moment(date).startOf('day').add(shiftStartSecondsSinceMidnight, 'seconds')
  const end = start.clone().add(shiftLengthSeconds, 'seconds')
  const timespan = `${start.format(format)} - ${end.format(format)}`
  const recursOnDaysDescription = _getRecursOnDaysDescription(daysOfWeek)
  return {
    timespan,
    description: recursOnDaysDescription,
  }
}
